/**
 * Question Page Component
 *
 * Renderiza questão individual com SSR support
 */

import { useState, useEffect } from 'react'
import type { QuestionMeta } from '@/shared/types/question'

interface QuestionPageProps {
  initialQuestion?: QuestionMeta
}

export default function QuestionPage({ initialQuestion }: QuestionPageProps) {
  const [meta, setMeta] = useState<QuestionMeta | null>(initialQuestion || null)
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null)
  const [showExplanation, setShowExplanation] = useState(false)

  useEffect(() => {
    // Hydration check
    if (typeof window !== 'undefined') {
      console.log('[QuestionPage] Client-side hydrated')
      console.log('[QuestionPage] Meta:', meta)
    }
  }, [meta])

  if (!meta || !meta.question) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Carregando questão...</p>
      </div>
    )
  }

  const { question } = meta

  const handleAnswerClick = (answerId: string) => {
    setSelectedAnswer(answerId)
    setShowExplanation(true)
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto', fontFamily: 'system-ui, sans-serif' }}>
      {/* Breadcrumb */}
      {meta.breadcrumb && meta.breadcrumb.length > 0 && (
        <nav style={{ marginBottom: '1rem', fontSize: '0.875rem', color: '#666' }}>
          {meta.breadcrumb.map((item, index) => (
            <span key={index}>
              {index > 0 && ' > '}
              <a href={item.url} style={{ color: '#3b82f6', textDecoration: 'none' }}>
                {item.name}
              </a>
            </span>
          ))}
        </nav>
      )}

      {/* Category & Topic Tags */}
      <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        {question.category && (
          <span
            style={{
              padding: '0.25rem 0.75rem',
              background: '#3b82f6',
              color: 'white',
              borderRadius: '4px',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            {question.category.name}
          </span>
        )}
        {question.topic && (
          <span
            style={{
              padding: '0.25rem 0.75rem',
              background: '#6366f1',
              color: 'white',
              borderRadius: '4px',
              fontSize: '0.875rem',
            }}
          >
            {question.topic.name}
          </span>
        )}
        {question.difficulty && (
          <span
            style={{
              padding: '0.25rem 0.75rem',
              background: question.difficulty === 'easy' ? '#10b981' : question.difficulty === 'medium' ? '#f59e0b' : '#ef4444',
              color: 'white',
              borderRadius: '4px',
              fontSize: '0.875rem',
            }}
          >
            {question.difficulty === 'easy' ? 'Fácil' : question.difficulty === 'medium' ? 'Médio' : 'Difícil'}
          </span>
        )}
      </div>

      {/* Question Text */}
      <div
        style={{
          marginBottom: '2rem',
          padding: '1.5rem',
          background: '#f8fafc',
          borderRadius: '8px',
          border: '2px solid #e2e8f0',
        }}
      >
        <h1 style={{ fontSize: '1.5rem', lineHeight: 1.6, margin: 0, color: '#1e293b' }}>
          {question.question_text}
        </h1>
        {question.image_url && (
          <img
            src={question.image_url}
            alt="Imagem da questão"
            style={{ marginTop: '1rem', maxWidth: '100%', borderRadius: '8px' }}
          />
        )}
      </div>

      {/* Answers */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.125rem', marginBottom: '1rem', color: '#475569' }}>Alternativas:</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {question.answers.map((answer) => {
            const isSelected = selectedAnswer === answer.id
            const isCorrect = answer.is_correct
            const showCorrectness = showExplanation && isSelected

            return (
              <button
                key={answer.id}
                onClick={() => handleAnswerClick(answer.id)}
                disabled={showExplanation}
                style={{
                  padding: '1rem',
                  textAlign: 'left',
                  background: showCorrectness
                    ? isCorrect
                      ? '#d1fae5'
                      : '#fee2e2'
                    : isSelected
                    ? '#e0e7ff'
                    : 'white',
                  border: `2px solid ${
                    showCorrectness ? (isCorrect ? '#10b981' : '#ef4444') : isSelected ? '#6366f1' : '#e2e8f0'
                  }`,
                  borderRadius: '8px',
                  cursor: showExplanation ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  fontSize: '1rem',
                  lineHeight: 1.5,
                  color: '#1e293b',
                }}
              >
                {answer.text}
              </button>
            )
          })}
        </div>
      </div>

      {/* Explanation */}
      {showExplanation && (
        <div
          style={{
            padding: '1.5rem',
            background: '#fefce8',
            border: '2px solid #fde047',
            borderRadius: '8px',
            marginBottom: '2rem',
          }}
        >
          <h3 style={{ fontSize: '1.125rem', marginBottom: '0.75rem', color: '#854d0e' }}>Explicação:</h3>
          <p style={{ margin: 0, lineHeight: 1.6, color: '#713f12' }}>
            {question.explanation || 'Explicação não disponível para esta questão.'}
          </p>
        </div>
      )}

      {/* Debug Info (apenas em desenvolvimento) */}
      {typeof import.meta !== 'undefined' && import.meta.env?.DEV && (
        <details style={{ marginTop: '2rem' }}>
          <summary style={{ cursor: 'pointer', color: '#64748b', fontSize: '0.875rem' }}>Debug Info</summary>
          <pre
            style={{
              marginTop: '0.5rem',
              padding: '1rem',
              background: '#f1f5f9',
              borderRadius: '4px',
              fontSize: '0.75rem',
              overflow: 'auto',
            }}
          >
            {JSON.stringify(meta, null, 2)}
          </pre>
        </details>
      )}
    </div>
  )
}
